/**
 * Admin → Accounting tab.
 *
 * Reads every number from the server (which reads directly from the
 * existing `orders`, `refunds`, `disputes` collections + Stripe balance
 * transactions). No client-side math on money to avoid drift.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const PERIODS = [
  ["today", "Today"], ["month", "This Month"], ["last_month", "Last Month"],
  ["year", "This Year"], ["all", "All Time"],
];
const MDT = {
  paid_fulfilment: "Paid Fulfilment", replacement: "Replacement",
  compensation: "Compensation", gift: "Gift / Promo",
  other: "Other", unknown_needs_review: "Needs Review",
};

const fmt = (n) => (n === null || n === undefined || n === "") ? "N/A" : `£${(Number(n) || 0).toFixed(2)}`;
const fmtOrZero = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const na = (v) => (v === null || v === undefined || v === "" ? "N/A" : v);

function SupplierBlock({ period }) {
  const [d, setD] = useState(null);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAudit = () => {
    api.get(`/admin/accounting/supplier-audit`).then(r => setAudit(r.data)).catch(() => setAudit({}));
  };
  useEffect(() => {
    api.get(`/admin/accounting/supplier?period=${period}`).then(r => setD(r.data)).catch(() => setD({}));
    loadAudit();
  }, [period]);

  const reverify = async () => {
    if (!window.confirm("Re-hit OLD STORE /orders/{id} for up to 25 stalled orders?")) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/accounting/supplier-audit?verify=true&limit=25`);
      setAudit(data);
      const lv = data.live_verification || {};
      toast.success(`Refreshed ${lv.refreshed}/${lv.scanned} stalled orders${lv.still_stalled ? ` · ${lv.still_stalled} still pending at supplier` : ""}${(lv.failures || []).length ? ` · ${lv.failures.length} errors` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-verify failed"); }
    setBusy(false);
  };

  const refreshCosts = async () => {
    if (!window.confirm("Pull ACTUAL historical costs from OLD STORE for every supplier-linked order? This hits the OLD STORE API once per order (may take a couple of minutes on large ledgers) and overwrites the cost + status with what OLD STORE actually charged.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/accounting/supplier/refresh-costs", {});
      const delta = Number(data.total_delta_gbp || 0);
      toast.success(
        `Scanned ${data.scanned} · Updated ${data.updated} · ${data.cost_changed_count} cost changes · Δ £${delta.toFixed(2)}${data.errors ? ` · ${data.errors} errors` : ""}`,
        { duration: 6000 }
      );
      loadAudit();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    setBusy(false);
  };

  if (!d) return null;
  const s = d.summary || {};
  const a = audit || {};
  const w = a.wallet || {};
  return (
    <div className="vault-card p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="mono-caption">Supplier (OLD STORE)</div>
        {audit && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-[var(--av-text-dim)]">Completeness:</span>
            <span data-testid="supplier-completeness" className={`font-semibold text-sm ${a.completeness_score >= 99 ? "gold-text" : a.completeness_score >= 90 ? "purple-text" : "text-red-300"}`}>
              {a.completeness_score}%
            </span>
            <button onClick={refreshCosts} disabled={busy}
                    data-testid="supplier-refresh-costs"
                    title="Pull actual historical costs from OLD STORE for every supplier-linked order"
                    className="btn-gold text-[10px] disabled:opacity-40">
              {busy ? "Refreshing…" : "Refresh OLD STORE costs"}
            </button>
            <button onClick={reverify} disabled={busy}
                    data-testid="supplier-reverify"
                    className="btn-outline-gold text-[10px] disabled:opacity-40">
              {busy ? "Verifying…" : "Re-verify stalled"}
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
        <div><span className="text-[var(--av-text-dim)]">COGS: </span><span className="gold-text font-semibold" data-testid="sup-cogs">{fmtOrZero(s.cogs_gbp)}</span></div>
        <div><span className="text-[var(--av-text-dim)]">Unknown Cost: </span><span className="text-red-300 font-semibold">{s.unknown_cost_count || 0}</span></div>
        <div><span className="text-[var(--av-text-dim)]">Manual COGS: </span><span className="purple-text font-semibold">{fmtOrZero(s.manual_dispatch_cost_gbp)}</span></div>
        <div><span className="text-[var(--av-text-dim)]">Linked / Unlinked: </span><span className="font-semibold">{s.linked_supplier_txns || 0} / <span className="text-red-300">{s.unlinked_supplier_txns || 0}</span></span></div>
        <div><span className="text-[var(--av-text-dim)]">Wallet top-ups: </span><span className="gold-text font-semibold">{fmtOrZero(s.wallet_topups_gbp)}</span></div>
        <div><span className="text-[var(--av-text-dim)]">Wallet purchases: </span><span className="purple-text font-semibold">{fmtOrZero(s.wallet_purchases_gbp)}</span></div>
        <div><span className="text-[var(--av-text-dim)]">Wallet balance: </span><span className="gold-text font-semibold">{s.wallet_closing_balance_gbp != null ? fmt(s.wallet_closing_balance_gbp) : "N/A"}</span></div>
        <div className="text-[10px] text-[var(--av-text-dim)]">Last poll: {(s.wallet_last_polled_at || "").slice(0,16).replace("T"," ") || "N/A"}</div>
      </div>

      {audit && (
        <div className="mb-4 border-t border-[var(--av-border)] pt-3">
          <div className="mono-caption text-[10px] mb-2 text-[var(--av-text-dim)]">Supplier ledger — every fulfilment bucketed & flagged</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
            <div><span className="text-[var(--av-text-dim)]">Total supplier orders: </span><span className="gold-text font-semibold" data-testid="sup-audit-total">{a.total_supplier_orders}</span></div>
            <div><span className="text-[var(--av-text-dim)]">Fully reconciled: </span><span className="gold-text font-semibold">{a.fully_reconciled}</span></div>
            <div><span className="text-[var(--av-text-dim)]">Wallet net delta: </span><span className="font-semibold">{fmtOrZero(w.cumulative_net_delta_gbp)}</span></div>
            <div><span className="text-[var(--av-text-dim)]">Wallet polls: </span><span className="font-semibold">{w.poll_count ?? "N/A"}</span></div>
          </div>

          {/* Red flags */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
            <div>
              <span className="text-[var(--av-text-dim)]">Paid, no supplier link: </span>
              <span className={`font-semibold ${a.paid_no_supplier_link ? "text-red-300" : "gold-text"}`} data-testid="sup-audit-no-link">
                {a.paid_no_supplier_link === 0 ? "0 · clean" : a.paid_no_supplier_link}
              </span>
            </div>
            <div>
              <span className="text-[var(--av-text-dim)]">Submitted, no ID: </span>
              <span className={`font-semibold ${a.submitted_no_id ? "text-red-300" : "gold-text"}`}>
                {a.submitted_no_id === 0 ? "0 · clean" : a.submitted_no_id}
              </span>
            </div>
            <div>
              <span className="text-[var(--av-text-dim)]">Stalled &gt; 24h: </span>
              <span className={`font-semibold ${a.stalled_orders ? "text-red-300" : "gold-text"}`} data-testid="sup-audit-stalled">
                {a.stalled_orders === 0 ? "0 · clean" : a.stalled_orders}
              </span>
            </div>
            <div>
              <span className="text-[var(--av-text-dim)]">Local ≠ API status: </span>
              <span className={`font-semibold ${a.status_drift ? "text-red-300" : "gold-text"}`}>
                {a.status_drift === 0 ? "0 · clean" : a.status_drift}
              </span>
            </div>
            <div>
              <span className="text-[var(--av-text-dim)]">Unknown cost: </span>
              <span className={`font-semibold ${a.unknown_cost ? "text-red-300" : "gold-text"}`}>
                {a.unknown_cost === 0 ? "0 · clean" : a.unknown_cost}
              </span>
            </div>
            <div>
              <span className="text-[var(--av-text-dim)]">Manual dispatches to classify: </span>
              <span className={`font-semibold ${a.manual_needs_classification ? "text-red-300" : "gold-text"}`}>
                {a.manual_needs_classification === 0 ? "0 · clean" : a.manual_needs_classification}
              </span>
            </div>
          </div>

          {/* Buckets */}
          <div className="grid md:grid-cols-3 gap-3 text-xs">
            <div>
              <div className="mono-caption text-[10px] mb-1 text-[var(--av-text-dim)]">By local status</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(a.by_status || {}).map(([k, v]) => (
                  <span key={k} className="pill pill-purple text-[10px]">{k}: {v}</span>
                ))}
                {Object.keys(a.by_status || {}).length === 0 && <span className="text-[var(--av-text-dim)]">N/A</span>}
              </div>
            </div>
            <div>
              <div className="mono-caption text-[10px] mb-1 text-[var(--av-text-dim)]">By OLD STORE API status</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(a.by_status_api || {}).map(([k, v]) => (
                  <span key={k} className="pill text-[10px] bg-emerald-500/20 text-emerald-300">{k}: {v}</span>
                ))}
                {Object.keys(a.by_status_api || {}).length === 0 && <span className="text-[var(--av-text-dim)]">N/A</span>}
              </div>
            </div>
            <div>
              <div className="mono-caption text-[10px] mb-1 text-[var(--av-text-dim)]">By cost source</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(a.by_cost_source || {}).map(([k, v]) => (
                  <span key={k} className={`pill text-[10px] ${k === "unknown" ? "bg-red-500/20 text-red-300" : "pill-purple"}`}>{k}: {v}</span>
                ))}
                {Object.keys(a.by_cost_source || {}).length === 0 && <span className="text-[var(--av-text-dim)]">N/A</span>}
              </div>
            </div>
          </div>

          {a.live_verification?.enabled && (
            <div className="mt-3 text-[11px] text-[var(--av-text-dim)]">
              Last re-verify: scanned {a.live_verification.scanned}, refreshed {a.live_verification.refreshed}
              {a.live_verification.still_stalled ? `, ${a.live_verification.still_stalled} still pending at supplier` : ""}
              {(a.live_verification.failures || []).length ? `, ${a.live_verification.failures.length} errors` : ""}
            </div>
          )}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="vault-table text-xs">
          <thead><tr><th>Date</th><th>AV Order</th><th>Supplier ID</th><th>Product</th><th>Qty</th><th>Cost (GBP)</th><th>Orig.</th><th>Source</th><th>Status</th></tr></thead>
          <tbody>
            {(d.rows || []).map(o => (
              <tr key={o.id}>
                <td>{((o.paid_at || o.created_at) || "").slice(0,16).replace("T"," ") || "N/A"}</td>
                <td className="font-mono">{o.id}{o.manual_dispatch ? " (MD)" : ""}</td>
                <td className="font-mono text-[10px]">{na(o.supplier_order_id)}</td>
                <td>{na(o.supplier_product_slug)}</td>
                <td>{o.supplier_quantity ?? "N/A"}</td>
                <td className="gold-text">{fmt(o.supplier_cost_gbp)}</td>
                <td className="text-[var(--av-text-dim)]">{o.supplier_cost_original_amount ? `${o.supplier_cost_original_amount} ${o.supplier_cost_currency || ""}` : "N/A"}</td>
                <td>{o.supplier_needs_review ? <span className="pill text-[10px] bg-red-500/20 text-red-300">Needs Review</span> : <span className="pill pill-purple text-[10px]">{o.supplier_cost_source || "N/A"}</span>}</td>
                <td>{o.supplier_status_api || o.supplier_status || "N/A"}</td>
              </tr>
            ))}
            {(d.rows || []).length === 0 && <tr><td colSpan="9" className="text-center py-4 text-[var(--av-text-dim)]">No supplier transactions</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Accounting() {
  const [period, setPeriod] = useState("month");
  const [summary, setSummary] = useState(null);
  const [country, setCountry] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [txns, setTxns] = useState([]);
  const [manual, setManual] = useState({rows: [], buckets: {}});
  const [auditData, setAuditData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = async () => {
    try {
      const [s, c, m, t, mm, au] = await Promise.all([
        api.get(`/admin/accounting/summary?period=${period}`),
        api.get(`/admin/accounting/country?period=${period}`),
        api.get(`/admin/accounting/monthly?period=${period === "month" || period === "last_month" ? "year" : period}`),
        api.get(`/admin/accounting/transactions?period=${period}&limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        api.get(`/admin/accounting/manual-dispatches?period=${period}`),
        api.get(`/admin/accounting/audit?stripe_days=90`),
      ]);
      setSummary(s.data); setCountry(c.data.rows || []);
      setMonthly(m.data.rows || []); setTxns(t.data.rows || []);
      setManual(mm.data);
      setAuditData(au.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const backfill = async (force = false) => {
    if (!window.confirm(force ? "Force re-enrich EVERY paid order from Stripe? This may take a few minutes." : "Backfill Stripe fees / country / disputes for any orders not yet reconciled?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/accounting/backfill", { force });
      const rem = data.orders_remaining || 0;
      toast.success(
        `Reconciled ${data.orders_scanned}/${data.orders_total} orders · ${data.orders_enriched} newly enriched · ${data.refunds_scanned}/${data.refunds_total} refunds · ${data.disputes_imported} disputes${rem ? ` · ${rem} still to walk` : " · complete"}${data.errors ? ` · ${data.errors} errors` : ""}`
      );
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
    setBusy(false);
  };

  const exportZip = async () => {
    const { data } = await api.get(`/admin/accounting/export?period=${period}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/zip" }));
    const a = document.createElement("a");
    a.href = url; a.download = `accounting-${period}.zip`; a.click();
    URL.revokeObjectURL(url);
  };

  const classify = async (order_id, t) => {
    await api.post(`/admin/accounting/manual-dispatches/${order_id}/classify`,
                    { manual_dispatch_type: t });
    toast.success(`Classified as ${MDT[t]}`);
    load();
  };

  if (!summary) return <div className="py-10 text-center text-[var(--av-text-dim)] text-sm">Loading accounting…</div>;

  const stat = (label, val, color = "gold", testid) => (
    <div className="vault-card p-4 min-w-0">
      <div className="mono-caption text-[10px] text-[var(--av-text-dim)] mb-1">{label}</div>
      <div data-testid={testid} className={`font-serif text-2xl font-bold truncate ${color === "gold" ? "gold-text" : color === "red" ? "text-red-300" : "purple-text"}`}>{val}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header controls */}
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="accounting-period" className="input-vault text-sm w-auto" value={period}
                onChange={e => setPeriod(e.target.value)}>
          {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button data-testid="accounting-backfill" onClick={() => backfill(false)}
                disabled={busy} className="btn-outline-gold text-xs disabled:opacity-40">
          {busy ? "Syncing…" : "Sync / Reconcile Stripe"}
        </button>
        <button onClick={() => backfill(true)} disabled={busy}
                className="btn-outline-gold text-xs disabled:opacity-40 opacity-70">
          Force re-enrich all
        </button>
        <button data-testid="accounting-export" onClick={exportZip} className="btn-gold text-xs">
          Export CSV bundle
        </button>
        <span className="text-[10px] text-[var(--av-text-dim)] ml-auto">
          {summary.last_sync_at ? `Last sync: ${summary.last_sync_at.slice(0, 16).replace("T", " ")}` : "Never synced"}
        </span>
      </div>

      {/* Summary — 6 essentials only. Everything else lives behind "Show detail". */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stat("Sales (net of refunds)", fmt(summary.net_sales_gbp), "gold", "acc-net-sales")}
        {stat("Refunds", fmt(summary.refunds_gbp), "red")}
        {stat("Supplier COGS", fmt(summary.cogs_gbp), "purple", "acc-cogs")}
        {stat("Stripe Fees", fmt(summary.stripe_fees_gbp), "purple")}
        {stat("Net Profit", fmt(summary.net_profit_gbp), "gold", "acc-net-profit")}
        {stat("Paid Orders", summary.transaction_count, "purple")}
      </div>
      <div className="text-[11px] text-[var(--av-text-dim)] -mt-2">
        <span className="font-mono">Net Profit</span> = Sales − Supplier COGS − Stripe Fees − Manual dispatch <span className="opacity-70">(VAT handled externally)</span>
        {summary.disputes_count > 0 && <> · Disputes <span className="text-red-300">{summary.disputes_count} · {fmt(summary.disputes_amount_gbp)}</span></>}
        {summary.manual_dispatch_count > 0 && <> · Manual dispatches {summary.manual_dispatch_count} · {fmt(summary.manual_dispatch_cogs_gbp)}</>}
      </div>

      {/* Supplier block — supplier COGS + wallet + audit */}
      <SupplierBlock period={period}/>

      {/* Manual dispatches */}
      <div className="vault-card p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="mono-caption">Manual Dispatches ({manual.count || 0})</div>
          <div className="text-[10px] text-[var(--av-text-dim)] flex flex-wrap gap-2">
            {Object.entries(manual.buckets || {}).map(([k, v]) => (
              <span key={k} className="pill pill-purple text-[10px]">{MDT[k]}: {v}</span>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="vault-table text-xs">
            <thead><tr><th>Date</th><th>Order</th><th>Product</th><th>Governor UID</th><th>Type</th><th>Note</th></tr></thead>
            <tbody>
              {(manual.rows || []).map(m => (
                <tr key={m.id}>
                  <td>{(m.created_at || "").slice(0,16).replace("T"," ") || "N/A"}</td>
                  <td className="font-mono">{m.id}</td>
                  <td>{(m.package_snapshot || {}).name || "N/A"}</td>
                  <td className="font-mono">{(m.game_profile_snapshot || {}).governor_uid || "N/A"}</td>
                  <td>
                    <select data-testid={`mdt-${m.id}`} className="input-vault text-xs py-1"
                            defaultValue={m.manual_dispatch_type || "unknown_needs_review"}
                            onChange={e => classify(m.id, e.target.value)}>
                      {Object.entries(MDT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </td>
                  <td className="text-[var(--av-text-dim)] truncate max-w-[220px]">{m.manual_dispatch_note || "N/A"}</td>
                </tr>
              ))}
              {(manual.rows || []).length === 0 && <tr><td colSpan="6" className="text-center py-4 text-[var(--av-text-dim)]">No manual dispatches</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transactions */}
      <div className="vault-card p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <div className="mono-caption">Transactions</div>
          <input data-testid="acc-search" className="input-vault text-xs w-64" placeholder="Order ID / PI / Charge"
                  value={q} onChange={e => setQ(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && load()}/>
          <button onClick={load} className="btn-outline-gold text-xs">Search</button>
        </div>
        <div className="overflow-x-auto">
          <table className="vault-table text-xs">
            <thead><tr><th>Paid</th><th>Order</th><th>Product</th><th>GBP</th><th>Country</th><th>PI</th></tr></thead>
            <tbody>
              {txns.map(o => (
                <tr key={o.id}>
                  <td>{(o.paid_at || "").slice(0,16).replace("T"," ") || "N/A"}</td>
                  <td className="font-mono">{o.id}</td>
                  <td className="truncate max-w-[180px]">{(o.package_snapshot || {}).name || "N/A"}</td>
                  <td>{fmt(o.cash_to_pay)}</td>
                  <td className="font-mono">{o.billing_country || o.card_country || "N/A"}</td>
                  <td className="font-mono text-[10px] truncate max-w-[160px]">{o.stripe_payment_intent_id || "N/A"}</td>
                </tr>
              ))}
              {txns.length === 0 && <tr><td colSpan="6" className="text-center py-4 text-[var(--av-text-dim)]">No transactions in this window.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Advanced — Ledger completeness + Reconciliation + Country/Monthly + Stripe cross-check.
          Hidden by default so the top of the page stays clean; expand when you need to audit. */}
      <div className="vault-card p-3">
        <button data-testid="acc-advanced-toggle"
                onClick={() => setShowAdvanced(v => !v)}
                className="w-full flex items-center justify-between text-left text-sm hover:text-[var(--av-gold)] transition-colors">
          <span className="mono-caption">Advanced · Audit & Reconciliation</span>
          <span className="text-[10px] text-[var(--av-text-dim)]">
            {showAdvanced ? "Hide" : "Show"} · ledger completeness, Stripe cross-check, by country / month
          </span>
        </button>
      </div>

      {showAdvanced && (
        <div className="space-y-4" data-testid="acc-advanced-panel">
          {/* Ledger completeness — proves every order is accounted for */}
          {auditData && (
            <div className="vault-card p-4" data-testid="ledger-completeness">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="mono-caption">Ledger Completeness</div>
                <div className="text-[10px] text-[var(--av-text-dim)]">
                  Every order in the DB, bucketed. Cross-checked against Stripe PIs (last {auditData.stripe_check?.window_days || 90}d).
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                <div><span className="text-[var(--av-text-dim)]">Total orders: </span><span className="gold-text font-semibold" data-testid="audit-total">{auditData.total_orders}</span></div>
                <div><span className="text-[var(--av-text-dim)]">Enriched: </span><span className="gold-text font-semibold">{auditData.total_enriched}</span></div>
                <div><span className="text-[var(--av-text-dim)]">Completeness: </span><span className={`font-semibold ${auditData.completeness_score >= 99 ? "gold-text" : auditData.completeness_score >= 90 ? "purple-text" : "text-red-300"}`} data-testid="audit-score">{auditData.completeness_score}%</span></div>
                <div><span className="text-[var(--av-text-dim)]">Refunds enriched: </span><span className="purple-text font-semibold">{auditData.refunds_enriched} / {auditData.refunds_total}</span></div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                <div>
                  <span className="text-[var(--av-text-dim)]">Paid without PI: </span>
                  <span className={`font-semibold ${auditData.paid_without_pi ? "text-red-300" : "gold-text"}`} data-testid="audit-paid-no-pi">
                    {auditData.paid_without_pi === 0 ? "0 · clean" : auditData.paid_without_pi}
                  </span>
                </div>
                <div>
                  <span className="text-[var(--av-text-dim)]">PI not enriched: </span>
                  <span className={`font-semibold ${auditData.pi_without_enrichment ? "text-red-300" : "gold-text"}`}>
                    {auditData.pi_without_enrichment === 0 ? "0 · clean" : auditData.pi_without_enrichment}
                  </span>
                </div>
                <div>
                  <span className="text-[var(--av-text-dim)]">MD needs classify: </span>
                  <span className={`font-semibold ${auditData.manual_needs_classification ? "text-red-300" : "gold-text"}`}>
                    {auditData.manual_needs_classification === 0 ? "0 · clean" : auditData.manual_needs_classification}
                  </span>
                </div>
                <div>
                  <span className="text-[var(--av-text-dim)]">Paid, no supplier link: </span>
                  <span className={`font-semibold ${auditData.supplier_unlinked_paid ? "text-red-300" : "gold-text"}`}>
                    {auditData.supplier_unlinked_paid === 0 ? "0 · clean" : auditData.supplier_unlinked_paid}
                  </span>
                </div>
              </div>

              <div className="mono-caption text-[10px] mb-2 text-[var(--av-text-dim)]">Every order bucketed by payment status</div>
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.entries(auditData.by_status || {}).map(([k, v]) => (
                  <span key={k} className="pill text-[10px] pill-purple">{k}: {v}</span>
                ))}
              </div>

              <div className="mono-caption text-[10px] mb-2 text-[var(--av-text-dim)]">Stripe → local cross-check (proof nothing is missing)</div>
              {auditData.stripe_check?.enabled ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-[var(--av-text-dim)]">Succeeded PIs in Stripe: </span><span className="gold-text font-semibold">{auditData.stripe_check.succeeded_in_stripe}</span></div>
                  <div><span className="text-[var(--av-text-dim)]">Matched to local orders: </span><span className="gold-text font-semibold">{auditData.stripe_check.matched_to_local}</span></div>
                  <div>
                    <span className="text-[var(--av-text-dim)]">Unmatched: </span>
                    <span className={`font-semibold ${auditData.stripe_check.unmatched_pi_count ? "text-red-300" : "gold-text"}`} data-testid="audit-stripe-unmatched">
                      {auditData.stripe_check.unmatched_pi_count === 0 ? "0 · clean" : auditData.stripe_check.unmatched_pi_count}
                    </span>
                  </div>
                  <div><span className="text-[var(--av-text-dim)]">Window: </span><span className="font-mono">{auditData.stripe_check.window_days}d</span></div>
                </div>
              ) : (
                <div className="text-xs text-[var(--av-text-dim)]">Stripe check unavailable: {auditData.stripe_check?.reason || "N/A"}</div>
              )}

              {auditData.stripe_check?.unmatched_pi_sample?.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <div className="mono-caption text-[10px] mb-1 text-red-300">Orphan Stripe PIs (money in Stripe, no local order)</div>
                  <table className="vault-table text-xs">
                    <thead><tr><th>PI ID</th><th>Amount</th><th>Currency</th><th>Created</th></tr></thead>
                    <tbody>
                      {auditData.stripe_check.unmatched_pi_sample.map(p => (
                        <tr key={p.id}>
                          <td className="font-mono text-[10px]">{p.id}</td>
                          <td>{fmtOrZero((p.amount || 0) / 100)}</td>
                          <td className="font-mono">{(p.currency || "").toUpperCase() || "N/A"}</td>
                          <td className="font-mono text-[10px]">{p.created ? new Date(p.created * 1000).toISOString().slice(0, 16).replace("T", " ") : "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Reconciliation status */}
          <div className="vault-card p-4">
            <div className="mono-caption mb-2">Reconciliation</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-[var(--av-text-dim)]">Matched: </span><span className="gold-text font-semibold">{summary.matched_orders ?? "N/A"}</span></div>
              <div><span className="text-[var(--av-text-dim)]">Unmatched: </span><span className="text-red-300 font-semibold">{summary.unmatched_orders ?? "N/A"}</span></div>
              <div><span className="text-[var(--av-text-dim)]">First txn: </span>{summary.first_transaction_at?.slice(0,10) || "N/A"}</div>
              <div><span className="text-[var(--av-text-dim)]">Last txn: </span>{summary.last_transaction_at?.slice(0,10) || "N/A"}</div>
            </div>
          </div>

          {/* Country + Monthly */}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="vault-card p-4">
              <div className="mono-caption mb-2">By Country</div>
              <div className="overflow-x-auto">
                <table className="vault-table text-xs">
                  <thead><tr><th>Country</th><th>#</th><th>Gross</th><th>Fees</th><th>Net</th></tr></thead>
                  <tbody>
                    {country.map((c) => (
                      <tr key={c.country}>
                        <td className="font-mono">{c.country}</td>
                        <td>{c.count}</td>
                        <td>{fmtOrZero(c.gross_gbp)}</td>
                        <td>{fmtOrZero(c.fee_gbp)}</td>
                        <td>{fmtOrZero(c.net_gbp)}</td>
                      </tr>
                    ))}
                    {country.length === 0 && <tr><td colSpan="5" className="text-center py-4 text-[var(--av-text-dim)]">No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="vault-card p-4">
              <div className="mono-caption mb-2">By Month</div>
              <div className="overflow-x-auto">
                <table className="vault-table text-xs">
                  <thead><tr><th>Month</th><th>#</th><th>Gross</th><th>Fees</th><th>Net</th></tr></thead>
                  <tbody>
                    {monthly.map((m) => (
                      <tr key={m.month}><td className="font-mono">{m.month}</td><td>{m.count}</td>
                        <td>{fmtOrZero(m.gross_gbp)}</td><td>{fmtOrZero(m.fee_gbp)}</td><td>{fmtOrZero(m.net_gbp)}</td></tr>
                    ))}
                    {monthly.length === 0 && <tr><td colSpan="5" className="text-center py-4 text-[var(--av-text-dim)]">No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
